import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import FollowUp from '../models/FollowUp';
import { completeFollowUp } from '../services/follow-up-service';

interface CompleteFollowUpModalProps {
    isOpen: boolean;
    onClose: () => void;
    onError: (message: string) => void;
    onComplete: (followUpId: number) => void;
    followUp: FollowUp;
    clientId: number;
}

const CompleteFollowUpModal: React.FC<CompleteFollowUpModalProps> = ({
                                                                         isOpen,
                                                                         onClose,
                                                                         onError,
                                                                         onComplete,
                                                                         followUp,
                                                                         clientId
                                                                     }) => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!isOpen) {
            setLoading(false);
        }
    }, [isOpen]);

    const handleComplete = async () => {
        setLoading(true);
        try {
            if (followUp.id) {
                const response = await completeFollowUp(followUp.id);
                if (response.status === 204) {
                    onComplete(followUp.id);
                } else {
                    onError('Unable to complete follow-up. Please try again later.');
                }
            }
        } catch (err: any) {
            console.error('Error completing follow-up:', err);
            onError('Unable to complete follow-up. Please try again later.');
        } finally {
            setLoading(false);
            onClose();
        }
    };

    const handleCompleteAndRecord = async () => {
        setLoading(true);
        try {
            if (followUp.id) {
                const response = await completeFollowUp(followUp.id);
                if (response.status === 204) {
                    navigate(`/interaction/new/${clientId}`, {state: {followUp}});
                } else {
                    onError('Unable to complete follow-up. Please try again.');
                }
            }
        } catch (err: any) {
            console.error('Error completing follow-up:', err);
            onError('Unable to complete follow-up. Please try again.');
        } finally {
            setLoading(false);
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
                aria-hidden="true"
                onClick={onClose}
            />

            <div className="relative bg-white w-full max-w-md p-6 rounded-lg shadow-lg overflow-auto max-h-screen">
                <Dialog.Title className="text-xl font-semibold mb-4">Complete Follow-Up</Dialog.Title>
                <p className="mb-6">What would you like to do with this follow-up?</p>

                <div className="flex flex-col space-y-3" data-testid="complete-and-close">
                    <button
                        onClick={handleComplete}
                        disabled={loading}
                        className="w-full px-4 py-2 rounded bg-crumbblue text-white font-medium shadow hover:shadow-lg transition"
                    >
                        {loading ? 'Processing...' : 'Complete Follow-Up'}
                    </button>
                    <button
                        onClick={handleCompleteAndRecord}
                        disabled={loading}
                        className="w-full px-4 py-2 rounded bg-crumborange text-white font-medium shadow hover:shadow-lg transition"
                    >
                        {loading ? 'Processing...' : 'Complete & Record Interaction'}
                    </button>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="w-full px-4 py-2 rounded border text-gray-700 hover:bg-gray-100 transition"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </Dialog>
    );
};

export default CompleteFollowUpModal;
